import React, { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Select from 'react-select';
import _debounce from 'lodash/debounce';

import {
  useAddContactUsMutation,
  useLazyGetVisitorHostsQuery
} from '../../../redux/apis/ContactUsApi';
import { notification } from '../../../utils/notification';
import { TOAST_TYPE } from '../../../utils/constant/errorMessage';

import logoIcon from '../../../assets/images/LogoIcon.svg';

import './ContactUsPublic.scss';

const { TOAST_SUCCESS, TOAST_ERROR } = TOAST_TYPE;

// This check-in kiosk is for the AU office, so the host dropdown only lists AU
// staff. Change to 'IN' (or remove) to scope the list differently.
const HOST_LOCALITY = 'AU';

const PURPOSES = [
  { value: 'Meeting', label: 'Meeting', icon: 'icofont-users-alt-4' },
  { value: 'Interview', label: 'Interview', icon: 'icofont-briefcase' },
  { value: 'Delivery', label: 'Delivery', icon: 'icofont-fast-delivery' },
  { value: 'Contractor / Maintenance', label: 'Contractor', icon: 'icofont-tools-alt-2' },
  { value: 'Client Visit', label: 'Client Visit', icon: 'icofont-handshake-deal' },
  { value: 'Personal Visit', label: 'Personal', icon: 'icofont-user-alt-3' },
  { value: 'Other', label: 'Other', icon: 'icofont-ellipsis' }
];

const checkinSchema = yup.object().shape({
  name: yup.string().trim().required('Please enter your full name').max(100, 'Name is too long'),
  phone: yup
    .string()
    .trim()
    .required('Please enter your mobile number')
    .matches(/^[0-9+\-()\s]{7,20}$/, 'Please enter a valid mobile number'),
  email: yup.string().trim().email('Please enter a valid email').max(150, 'Email is too long'),
  company: yup.string().trim().max(150, 'Company name is too long'),
  hostName: yup.string().trim().required('Please select who you are here to meet'),
  purpose: yup.string().trim().required('Please select the purpose of your visit'),
  hasAppointment: yup
    .boolean()
    .typeError('Please select an option')
    .required('Please select an option'),
  expectedMeetingTime: yup.string().trim(),
  needsAccessibility: yup.boolean().default(false),
  accessibilityDetails: yup.string().when('needsAccessibility', {
    is: true,
    then: (s) => s.trim().required('Please provide the accessibility details'),
    otherwise: (s) => s.trim()
  }),
  agreeSafety: yup.boolean().oneOf([true], 'Required'),
  agreeBadge: yup.boolean().oneOf([true], 'Required'),
  agreeInfo: yup.boolean().oneOf([true], 'Required')
});

const defaultValues = {
  name: '',
  phone: '',
  email: '',
  company: '',
  hostId: '',
  hostName: '',
  purpose: '',
  hasAppointment: undefined,
  expectedMeetingTime: '',
  needsAccessibility: false,
  accessibilityDetails: '',
  agreeSafety: false,
  agreeBadge: false,
  agreeInfo: false
};

const hostSelectStyles = {
  control: (base) => ({ ...base, minHeight: 52 }),
  valueContainer: (base) => ({ ...base, padding: '2px 14px' })
};

const ContactUsPublic = () => {
  const [submitted, setSubmitted] = useState(false);
  const [visitorName, setVisitorName] = useState('');
  const [hostOptions, setHostOptions] = useState([]);
  const [addContactUs, { isLoading }] = useAddContactUsMutation();
  const [fetchHosts, { isFetching: hostsLoading }] = useLazyGetVisitorHostsQuery();

  const {
    reset,
    watch,
    control,
    register,
    setValue,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(checkinSchema),
    defaultValues
  });

  const needsAccessibility = watch('needsAccessibility');
  // const purpose = watch('purpose');

  const loadHosts = useMemo(
    () =>
      _debounce(async (search) => {
        try {
          const res = await fetchHosts({ search, limit: 20, locality: HOST_LOCALITY });
          const list = (res?.data?.data || []).map((h) => ({ value: h._id, label: h.name }));
          setHostOptions(list);
        } catch (e) {
          setHostOptions([]);
        }
      }, 300),
    [fetchHosts]
  );

  const onHostMenuOpen = () => {
    if (!hostOptions.length) loadHosts('');
  };

  const onSubmit = async (values) => {
    try {
      const payload = {
        name: values.name,
        phone: values.phone,
        email: values.email || undefined,
        company: values.company || undefined,
        hostId: values.hostId || undefined,
        hostName: values.hostName,
        purpose: values.purpose,
        hasAppointment: values.hasAppointment,
        needsAccessibility: values.needsAccessibility || false,
        accessibilityDetails: values.needsAccessibility ? values.accessibilityDetails : undefined,
        expectedMeetingTime: values.expectedMeetingTime || undefined,
        agreementAccepted: values.agreeSafety && values.agreeBadge && values.agreeInfo
      };
      const res = await addContactUs(payload);
      if (res?.data?.success) {
        notification(res?.data?.statusMessage, TOAST_SUCCESS);
        setVisitorName(values.name);
        reset(defaultValues);
        setHostOptions([]);
        setSubmitted(true);
      } else {
        notification(
          res?.error?.data?.statusMessage || 'Something went wrong. Please try again.',
          TOAST_ERROR
        );
      }
    } catch (error) {
      notification(error?.message || 'Something went wrong. Please try again.', TOAST_ERROR);
    }
  };

  const YesNo = ({ name, label, required }) => (
    <div className="gc-field">
      <label className="gc-label">
        {label} {required && <span className="gc-req">*</span>}
      </label>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <div className="gc-toggle">
            <button
              type="button"
              className={`gc-toggle__btn ${field.value === true ? 'is-active' : ''}`}
              onClick={() => field.onChange(true)}
            >
              <i className="icofont-check-circled" aria-hidden="true" />
              Yes
            </button>
            <button
              type="button"
              className={`gc-toggle__btn ${field.value === false ? 'is-active' : ''}`}
              onClick={() => field.onChange(false)}
            >
              <i className="icofont-close-circled" aria-hidden="true" />
              No
            </button>
          </div>
        )}
      />
      {errors[name] && <small className="gc-error">{errors[name].message}</small>}
    </div>
  );

  return (
    <div className="gc">
      <div className="gc__card">
        {/* ---------------- Left / brand panel ---------------- */}
        <aside className="gc__aside">
          <div className="gc__aside-inner">
            <div className="gc__brand">
              <span className="gc__brand-mark">
                <img src={logoIcon} alt="Software Co" />
              </span>
              <span className="gc__brand-name">Software Co</span>
            </div>

            <div className="gc__aside-body">
              <span className="gc__badge">
                <i className="icofont-users-alt-5" aria-hidden="true" /> Visitor Check-In
              </span>
              <h1 className="gc__aside-title">
                Welcome!
                <br />
                Let&apos;s get you signed in.
              </h1>
              <p className="gc__aside-text">
                Complete the short form and your host will be notified the moment you arrive. It
                only takes a minute.
              </p>

              <ul className="gc__steps">
                <li>
                  <span className="gc__step-num">1</span>
                  <div>
                    <strong>Your details</strong>
                    <span>Tell us who you are</span>
                  </div>
                </li>
                <li>
                  <span className="gc__step-num">2</span>
                  <div>
                    <strong>Your visit</strong>
                    <span>Who and why you&apos;re here</span>
                  </div>
                </li>
                <li>
                  <span className="gc__step-num">3</span>
                  <div>
                    <strong>Agreement</strong>
                    <span>Confirm &amp; check in</span>
                  </div>
                </li>
              </ul>
            </div>

            <div className="gc__aside-foot">
              <i className="icofont-shield-alt" aria-hidden="true" />
              Your information is kept secure and used only for this visit.
            </div>
          </div>
        </aside>

        {/* ---------------- Right / form panel ---------------- */}
        <main className="gc__main">
          {submitted ? (
            <div className="gc__success">
              <div className="gc__success-ring">
                <div className="gc__success-icon">
                  <i className="icofont-check" aria-hidden="true" />
                </div>
              </div>
              <h2>
                You&apos;re all checked in{visitorName ? `, ${visitorName.split(' ')[0]}` : ''}!
              </h2>
              <p>
                Please take a seat at reception — your host has been notified and will be with you
                shortly.
              </p>
              <button
                type="button"
                className="gc-submit gc-submit--ghost"
                onClick={() => setSubmitted(false)}
              >
                Check in another visitor
              </button>
            </div>
          ) : (
            <form className="gc__form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <div className="gc__form-body">
                {/* Visitor Details */}
                <section className="gc__section">
                  <div className="gc__section-head">
                    <span className="gc__section-index">01</span>
                    <h3>Visitor Details</h3>
                  </div>

                  <div className="gc__grid">
                    <div className="gc-field">
                      <label className="gc-label" htmlFor="v-name">
                        Full Name <span className="gc-req">*</span>
                      </label>
                      <div className="gc-input-wrap">
                        <i className="icofont-user-alt-7 gc-input-icon" aria-hidden="true" />
                        <input
                          id="v-name"
                          type="text"
                          placeholder="John Doe"
                          className={`gc-input ${errors.name ? 'is-invalid' : ''}`}
                          {...register('name')}
                        />
                      </div>
                      {errors.name && <small className="gc-error">{errors.name.message}</small>}
                    </div>

                    <div className="gc-field">
                      <label className="gc-label" htmlFor="v-phone">
                        Mobile Number <span className="gc-req">*</span>
                      </label>
                      <div className="gc-input-wrap">
                        <i className="icofont-smart-phone gc-input-icon" aria-hidden="true" />
                        <input
                          id="v-phone"
                          type="tel"
                          placeholder="+1 234 567 890"
                          className={`gc-input ${errors.phone ? 'is-invalid' : ''}`}
                          {...register('phone')}
                        />
                      </div>
                      {errors.phone && <small className="gc-error">{errors.phone.message}</small>}
                    </div>

                    <div className="gc-field">
                      <label className="gc-label" htmlFor="v-email">
                        Email Address
                      </label>
                      <div className="gc-input-wrap">
                        <i className="icofont-email gc-input-icon" aria-hidden="true" />
                        <input
                          id="v-email"
                          type="email"
                          placeholder="you@example.com"
                          className={`gc-input ${errors.email ? 'is-invalid' : ''}`}
                          {...register('email')}
                        />
                      </div>
                      {errors.email && <small className="gc-error">{errors.email.message}</small>}
                    </div>

                    <div className="gc-field">
                      <label className="gc-label" htmlFor="v-company">
                        Company / Organisation
                      </label>
                      <div className="gc-input-wrap">
                        <i className="icofont-building gc-input-icon" aria-hidden="true" />
                        <input
                          id="v-company"
                          type="text"
                          placeholder="Company name"
                          className={`gc-input ${errors.company ? 'is-invalid' : ''}`}
                          {...register('company')}
                        />
                      </div>
                      {errors.company && (
                        <small className="gc-error">{errors.company.message}</small>
                      )}
                    </div>
                  </div>
                </section>

                {/* Visit Details */}
                <section className="gc__section">
                  <div className="gc__section-head">
                    <span className="gc__section-index">02</span>
                    <h3>Visit Details</h3>
                  </div>

                  <div className="gc-field">
                    <label className="gc-label">
                      Who are you here to meet? <span className="gc-req">*</span>
                    </label>
                    <Select
                      classNamePrefix="cu-select"
                      styles={hostSelectStyles}
                      options={hostOptions}
                      isLoading={hostsLoading}
                      onMenuOpen={onHostMenuOpen}
                      onInputChange={(val) => {
                        loadHosts(val);
                        return val;
                      }}
                      onChange={(opt) => {
                        setValue('hostId', opt?.value || '', { shouldValidate: false });
                        setValue('hostName', opt?.label || '', { shouldValidate: true });
                      }}
                      placeholder="Search for an employee by name..."
                      noOptionsMessage={() => 'Start typing to search employees'}
                    />
                    {errors.hostName && (
                      <small className="gc-error">{errors.hostName.message}</small>
                    )}
                  </div>

                  <div className="gc-field">
                    <label className="gc-label">
                      Purpose of Visit <span className="gc-req">*</span>
                    </label>
                    <Controller
                      control={control}
                      name="purpose"
                      render={({ field }) => (
                        <div className="gc-purpose">
                          {PURPOSES.map((p) => (
                            <button
                              type="button"
                              key={p.value}
                              className={`gc-purpose__item ${field.value === p.value ? 'is-active' : ''}`}
                              onClick={() => field.onChange(p.value)}
                            >
                              <i className={p.icon} aria-hidden="true" />
                              <span>{p.label}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    />
                    {errors.purpose && <small className="gc-error">{errors.purpose.message}</small>}
                  </div>

                  <div className="gc__grid">
                    <YesNo
                      name="hasAppointment"
                      label="Do you have a scheduled appointment?"
                      required
                    />

                    <div className="gc-field">
                      <label className="gc-label" htmlFor="v-time">
                        Expected Meeting Time
                      </label>
                      <div className="gc-input-wrap">
                        <i className="icofont-clock-time gc-input-icon" aria-hidden="true" />
                        <input
                          id="v-time"
                          type="time"
                          className="gc-input"
                          {...register('expectedMeetingTime')}
                        />
                      </div>
                    </div>
                  </div>

                  <YesNo
                    name="needsAccessibility"
                    label="Do you require any accessibility assistance?"
                  />

                  {needsAccessibility === true && (
                    <div className="gc-field gc-fade-in">
                      <label className="gc-label" htmlFor="v-access">
                        Please provide details <span className="gc-req">*</span>
                      </label>
                      <textarea
                        id="v-access"
                        rows="3"
                        placeholder="Let us know how we can best assist you..."
                        className={`gc-input gc-textarea ${errors.accessibilityDetails ? 'is-invalid' : ''}`}
                        {...register('accessibilityDetails')}
                      />
                      {errors.accessibilityDetails && (
                        <small className="gc-error">{errors.accessibilityDetails.message}</small>
                      )}
                    </div>
                  )}
                </section>

                {/* Agreement */}
                <section className="gc__section">
                  <div className="gc__section-head">
                    <span className="gc__section-index">03</span>
                    <h3>Visitor Agreement</h3>
                  </div>

                  <div className="gc-agreement">
                    {[
                      {
                        name: 'agreeSafety',
                        text: "I agree to follow the organisation's workplace health, safety, security and emergency procedures."
                      },
                      {
                        name: 'agreeBadge',
                        text: 'I understand that I may be required to wear a visitor badge while on the premises.'
                      },
                      {
                        name: 'agreeInfo',
                        text: 'I confirm that the information provided above is correct.'
                      }
                    ].map((c) => (
                      <label className="gc-check" key={c.name}>
                        <input type="checkbox" {...register(c.name)} />
                        <span className="gc-check__box" aria-hidden="true">
                          <i className="icofont-check" />
                        </span>
                        <span className="gc-check__text">{c.text}</span>
                      </label>
                    ))}
                    {(errors.agreeSafety || errors.agreeBadge || errors.agreeInfo) && (
                      <small className="gc-error">Please accept all agreements to continue.</small>
                    )}
                  </div>
                </section>
              </div>

              <div className="gc__form-foot">
                <button type="submit" className="gc-submit" disabled={isLoading}>
                  {isLoading ? (
                    <span className="spinner-border spinner-border-sm" role="status" />
                  ) : (
                    <>
                      <i className="icofont-check-circled" aria-hidden="true" />
                      Check in
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </main>
      </div>
    </div>
  );
};

export default ContactUsPublic;
